import { useState, useCallback } from "react";
import { toast } from "react-toastify";
import {
  fetchMicrogridList,
  fetchMicrogridSysconfig,
  saveMicrogridSysconfig,
  uploadTariffCsv,
  parseCsvMetadata,
} from "../api/microgridsApi";
import {
  SUMMER_TRD_MONTHS,
  WINTER_TRD_MONTHS,
  TRD_HOUR_START,
  TRD_HOUR_END,
} from "../tou8Windows";

const INITIAL_FORM_DATA = {
  meter_Name: "",
  make_and_model: "",
  serial_number: "",
  demand_charge: "",
  demand_charge_escalator_fraction: "",
  csv_file_name: "",
  demand_charge_strategy: "none",
  ratchet_percentage: "",
  lookback_months: "",
  historical_peak_kw: "",
  // TOU-8 three-tier demand charges ($/kW): Facilities-Related Demand
  // (all hours) + Summer on-peak TRD (Jun-Sep wkdy 4-9pm) + Winter
  // mid-peak TRD (Oct-May wkdy 4-9pm).
  frd_rate: "",
  summer_trd_rate: "",
  winter_trd_rate: "",
};

// Time windows for the two TOU-8 Time-Related Demand (TRD) tiers. In the
// Data Center webapp these are derived from the SCE TOU-8 Option D tariff
// preset (siteWizard/tariffPresets.js, TARIFF_PRESETS.sce_tou8_d); prod has
// no analytics layer yet, so ../tou8Windows.js mirrors that preset's numbers
// and is the single source of truth here. The engine's time_of_use
// demand-charge schema (months 1-12, weekday-only, 24h hour_start/hour_end)
// is built from them below.

function validateMeterFields(formData, uploadMessage, existingPrimaryDemandCharge) {
  const errors = {};
  const alphaNumericRegex = /^(?=.*[a-zA-Z])(?=.*\d)[a-zA-Z0-9]{1,50}$/;
  const alphaNumericNoSpaceRegex = /^(?!\d+$)[a-zA-Z0-9_]{1,50}$/;

  if (!formData.meter_Name.trim()) {
    errors.meter_Name = "Device Name is required";
  } else if (!alphaNumericNoSpaceRegex.test(formData.meter_Name.trim())) {
    errors.meter_Name =
      "Meter name can only contain letters, numbers, and underscores (no spaces, hyphens, or special characters)";
  }

  if (
    formData.make_and_model.trim() &&
    !alphaNumericNoSpaceRegex.test(formData.make_and_model.trim())
  ) {
    errors.make_and_model = "Please enter a valid Make & Model";
  }

  if (
    formData.serial_number.trim() &&
    !alphaNumericRegex.test(formData.serial_number.trim())
  ) {
    errors.serial_number = "Please enter a valid serial number";
  }

  if (formData.demand_charge_strategy === "tou8") {
    const tierChecks = [
      ["frd_rate", "Facilities-Related Demand (FRD)"],
      ["summer_trd_rate", "Summer On-Peak TRD"],
      ["winter_trd_rate", "Winter Mid-Peak TRD"],
    ];
    for (const [key, label] of tierChecks) {
      const v = formData[key];
      if (v === undefined || v === "") {
        errors[key] = `${label} is required`;
      } else if (isNaN(Number(v))) {
        errors[key] = `${label} must be a number`;
      } else if (Number(v) < 0 || Number(v) > 100) {
        errors[key] = `${label} must be between 0 and 100`;
      }
    }
  } else if (formData.demand_charge_strategy !== "none") {
    if (formData.demand_charge === undefined || formData.demand_charge === "") {
      errors.demand_charge = "Demand Charge is required";
    } else if (isNaN(Number(formData.demand_charge))) {
      errors.demand_charge = "Demand Charge must be a number";
    } else {
      const val = Number(formData.demand_charge);
      if (val < 0 || val > 100) {
        errors.demand_charge = "Demand Charge must be between 0 and 100";
      }
    }
  }

  if (formData.demand_charge_strategy !== "none") {
    if (
      formData.demand_charge_escalator_fraction === undefined ||
      formData.demand_charge_escalator_fraction === ""
    ) {
      errors.demand_charge_escalator_fraction =
        "Demand Charge Escalator fraction is required";
    } else if (
      isNaN(formData.demand_charge_escalator_fraction) ||
      Number(formData.demand_charge_escalator_fraction) < 0 ||
      Number(formData.demand_charge_escalator_fraction) > 1
    ) {
      errors.demand_charge_escalator_fraction =
        "Demand Charge escalator fraction must be between 0 and 1";
    }

    if (
      formData.demand_charge_strategy === "ratcheted" ||
      // Prod-only guard (ahead of the Data Center version): under TOU-8 a
      // hydrated ratcheted primary keeps its ratchet fields, so validate them.
      (formData.demand_charge_strategy === "tou8" &&
        existingPrimaryDemandCharge?.type === "ratcheted")
    ) {
      if (formData.ratchet_percentage === undefined || formData.ratchet_percentage === "") {
        errors.ratchet_percentage = "Ratchet Percentage is required";
      } else if (
        isNaN(formData.ratchet_percentage) ||
        Number(formData.ratchet_percentage) < 0 ||
        Number(formData.ratchet_percentage) > 1
      ) {
        errors.ratchet_percentage = "Ratchet Percentage must be between 0 and 1";
      }

      if (formData.lookback_months === undefined || formData.lookback_months === "") {
        errors.lookback_months = "Lookback Months is required";
      } else if (
        isNaN(formData.lookback_months) ||
        Number(formData.lookback_months) < 1 ||
        !Number.isInteger(Number(formData.lookback_months))
      ) {
        errors.lookback_months = "Lookback Months must be a positive integer";
      }

      if (formData.historical_peak_kw === undefined || formData.historical_peak_kw === "") {
        errors.historical_peak_kw = "Historical Peak kW is required";
      } else if (
        isNaN(formData.historical_peak_kw) ||
        Number(formData.historical_peak_kw) < 0
      ) {
        errors.historical_peak_kw = "Historical Peak kW must be a positive number";
      }
    }
  }

  if (!formData.csv_file_name) {
    errors.csv_file_name = "Utility tariff Data File is required.";
  }
  if (uploadMessage?.type === "error") {
    errors.uploadMessage = uploadMessage.text;
  }

  return errors;
}

const safeFloat = (v) => {
  if (v === "" || v == null) return null;
  const n = parseFloat(v);
  return isNaN(n) ? null : n;
};
const safeInt = (v) => {
  if (v === "" || v == null) return null;
  const n = parseInt(v, 10);
  return isNaN(n) ? null : n;
};

// Build the PRIMARY demand charge (`meter.demand_charge`). For TOU-8 this
// is the Facilities-Related Demand (FRD): a noncoincident, all-hours,
// year-round charge.
function buildDemandChargeObject(formData, existingPrimaryDemandCharge) {
  if (formData.demand_charge_strategy === "none" || !formData.demand_charge_strategy) {
    return undefined;
  }

  const escalator = safeFloat(formData.demand_charge_escalator_fraction);

  if (formData.demand_charge_strategy === "tou8") {
    const rate = safeFloat(formData.frd_rate);
    if (rate === null || escalator === null) return undefined;
    // Prod-only guard (ahead of the Data Center version): the TRD tiers STACK
    // on top of the meter's existing primary demand charge, so an edited
    // TOU-8 meter keeps its original primary (type, name, ratchet fields)
    // and only the values the form edits change. A new meter emits the
    // Data Center shape below.
    if (existingPrimaryDemandCharge) {
      const obj = { ...existingPrimaryDemandCharge, rate, escalator_fraction: escalator };
      if (obj.type === "ratcheted") {
        obj.ratchet_percentage = safeFloat(formData.ratchet_percentage) ?? obj.ratchet_percentage;
        obj.lookback_months = safeInt(formData.lookback_months) ?? obj.lookback_months;
        obj.historical_peak_kw = safeFloat(formData.historical_peak_kw) ?? obj.historical_peak_kw;
      }
      return obj;
    }
    return { type: "noncoincident", rate, escalator_fraction: escalator };
  }

  const rate = safeFloat(formData.demand_charge);
  if (rate === null || escalator === null) return undefined;

  const obj = {
    type: formData.demand_charge_strategy,
    rate,
    escalator_fraction: escalator,
  };

  if (formData.demand_charge_strategy === "ratcheted") {
    obj.ratchet_percentage = safeFloat(formData.ratchet_percentage) ?? 0.8;
    obj.lookback_months = safeInt(formData.lookback_months) ?? 12;
    obj.historical_peak_kw = safeFloat(formData.historical_peak_kw) ?? 0;
  }

  return obj;
}

// Build the ADDITIONAL demand charges list (`meter.demand_charges`) — the
// two TOU-8 Time-Related Demand (TRD) tiers (summer on-peak + winter
// mid-peak), each a time_of_use charge. Returns undefined for non-TOU-8
// strategies so the list is cleared.
function buildDemandChargesList(formData, existingTouCharges) {
  if (formData.demand_charge_strategy !== "tou8") return undefined;

  const escalator = safeFloat(formData.demand_charge_escalator_fraction) ?? 0;
  const summer = safeFloat(formData.summer_trd_rate);
  const winter = safeFloat(formData.winter_trd_rate);
  const charges = [];

  // Prod-only guard (ahead of the Data Center version): a tier hydrated from
  // an existing time_of_use charge re-emits that charge's original object
  // (name, window, any other field) with only the form's rate/escalator
  // applied; a tier with no original emits the Data Center shape.
  if (summer !== null) {
    charges.push(
      existingTouCharges?.summer
        ? { ...existingTouCharges.summer, rate: summer, escalator_fraction: escalator }
        : {
            type: "time_of_use",
            rate: summer,
            escalator_fraction: escalator,
            months: SUMMER_TRD_MONTHS,
            weekdays_only: true,
            hour_start: TRD_HOUR_START,
            hour_end: TRD_HOUR_END,
          },
    );
  }
  if (winter !== null) {
    charges.push(
      existingTouCharges?.winter
        ? { ...existingTouCharges.winter, rate: winter, escalator_fraction: escalator }
        : {
            type: "time_of_use",
            rate: winter,
            escalator_fraction: escalator,
            months: WINTER_TRD_MONTHS,
            weekdays_only: true,
            hour_start: TRD_HOUR_START,
            hour_end: TRD_HOUR_END,
          },
    );
  }

  return charges.length > 0 ? charges : undefined;
}

export default function useAddMicrogrid({
  storageAccountName,
  accessToken,
  userId,
  customerPrefix,
  onSuccess,
}) {
  const [showModal, setShowModal] = useState(false);
  const [step, setStep] = useState(0);
  const [microgridName, setMicrogridName] = useState("");
  const [formData, setFormData] = useState(INITIAL_FORM_DATA);
  const [errors, setErrors] = useState({});
  const [submitError, setSubmitError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadMessage, setUploadMessage] = useState("");
  const [editingDesign, setEditingDesign] = useState(null);
  const [isEditingLoading, setIsEditingLoading] = useState(false);
  const [microgridBase, setMicrogridBase] = useState("");
  // Prod-only guard (ahead of the Data Center version): the original primary
  // demand_charge and the original summer/winter time_of_use charges of the
  // TOU-8 meter being edited, so the save stacks on them instead of
  // re-emitting the Data Center's noncoincident/nameless shape. null (and
  // {summer: null, winter: null}) for a new meter or a non-TOU-8 meter.
  const [existingPrimaryDemandCharge, setExistingPrimaryDemandCharge] = useState(null);
  const [existingTouCharges, setExistingTouCharges] = useState({ summer: null, winter: null });

  const openModal = useCallback(() => {
    setShowModal(true);
    setStep(1);
    setMicrogridName("");
    setFormData(INITIAL_FORM_DATA);
    setErrors({});
    setSubmitError("");
    setUploadMessage("");
    setExistingPrimaryDemandCharge(null);
    setExistingTouCharges({ summer: null, winter: null });
  }, []);

  const closeModal = useCallback(() => {
    setShowModal(false);
    setStep(0);
    setMicrogridName("");
    setFormData(INITIAL_FORM_DATA);
    setErrors({});
    setSubmitError("");
    setUploadMessage("");
    setEditingDesign(null);
    setExistingPrimaryDemandCharge(null);
    setExistingTouCharges({ summer: null, winter: null });
  }, []);

  const openEdit = useCallback(
    async (design) => {
      setEditingDesign(design);
      setShowModal(true);
      setStep(3);
      setErrors({});
      setSubmitError("");
      setUploadMessage("");
      setExistingPrimaryDemandCharge(null);
      setExistingTouCharges({ summer: null, winter: null });
      setIsEditingLoading(true);

      try {
        const fullSysconfig = await fetchMicrogridSysconfig({
          storageAccountName,
          simulationName: design.name,
          accessToken,
        });

        let meterDevice = null;
        const meters = fullSysconfig?.Microgrid?.meters;
        if (Array.isArray(meters) && meters.length > 0) {
          if (design.meterName) {
            meterDevice =
              meters.find((m) => (m.name || "").trim() === String(design.meterName).trim()) ||
              meters[0];
          } else {
            meterDevice = meters[0];
          }
        }

        if (!meterDevice && Array.isArray(fullSysconfig?.devices)) {
          const transfer = fullSysconfig.devices.find(
            (d) => d.device_type?.toLowerCase() === "transfer",
          );
          if (transfer) {
            const migratedMeter = {
              name: transfer.device_name ?? "",
              make_and_model: transfer.make_and_model ?? "",
              serial_number: transfer.serial_number ?? "",
              utility_tariff_csv: transfer.csv_file_name ?? "",
              building_load: { historical_load_csv: "" },
              evs: [],
              batteries: [],
              solar_arrays: [],
              fueled_generators: [],
              controllable_loads: [],
              uncontrollable_loads: [],
            };
            if (transfer.demand_charge && typeof transfer.demand_charge === "object") {
              migratedMeter.demand_charge = transfer.demand_charge;
            }
            await saveMicrogridSysconfig({
              storageAccountName,
              simulationName: design.name,
              accessToken,
              payload: { Microgrid: { meters: [migratedMeter] } },
            });
            meterDevice = migratedMeter;
          }
        }

        if (!meterDevice) {
          toast.error("Failed to load meter details for editing. Please try again.");
          closeModal();
          return;
        }

        let demandChargeStrategy = "none";
        let demandChargeRate = "";
        let demandEscalatorFraction = "";
        let ratchetPercentage = "";
        let lookbackMonths = "";
        let historicalPeakKw = "";
        let frdRate = "";
        let summerTrdRate = "";
        let winterTrdRate = "";

        if (meterDevice.demand_charge && typeof meterDevice.demand_charge === "object") {
          const dc = meterDevice.demand_charge;
          demandChargeStrategy = dc.type || "none";
          demandChargeRate = dc.rate ?? "";
          demandEscalatorFraction = dc.escalator_fraction ?? "";
          ratchetPercentage = dc.ratchet_percentage ?? "";
          lookbackMonths = dc.lookback_months ?? "";
          historicalPeakKw = dc.historical_peak_kw ?? "";
        }

        // TOU-8: a primary noncoincident demand_charge (FRD) plus a
        // demand_charges list of time_of_use TRD tiers. Detect it and map
        // back to the 3-tier editor (summer = a window covering Jun-Sep,
        // winter = everything else).
        const touList = Array.isArray(meterDevice.demand_charges)
          ? meterDevice.demand_charges.filter((c) => c?.type === "time_of_use")
          : [];
        if (touList.length > 0) {
          demandChargeStrategy = "tou8";
          frdRate = meterDevice.demand_charge?.rate ?? "";
          demandEscalatorFraction =
            meterDevice.demand_charge?.escalator_fraction ??
            touList[0]?.escalator_fraction ??
            "";
          // Prod-only guard (ahead of the Data Center version): keep the
          // original charge objects the tiers were read from.
          let summerCharge = null;
          let winterCharge = null;
          for (const c of touList) {
            const months = Array.isArray(c.months) ? c.months : [];
            const isSummer = months.includes(6) || months.includes(7) || months.includes(8);
            if (isSummer) {
              summerTrdRate = c.rate ?? "";
              summerCharge = c;
            } else {
              winterTrdRate = c.rate ?? "";
              winterCharge = c;
            }
          }
          setExistingPrimaryDemandCharge(
            meterDevice.demand_charge && typeof meterDevice.demand_charge === "object"
              ? meterDevice.demand_charge
              : null,
          );
          setExistingTouCharges({ summer: summerCharge, winter: winterCharge });
        }

        setFormData({
          meter_Name: meterDevice.name ?? "",
          make_and_model: meterDevice.make_and_model ?? "",
          serial_number: meterDevice.serial_number ?? "",
          demand_charge: demandChargeRate,
          demand_charge_escalator_fraction: demandEscalatorFraction,
          csv_file_name: meterDevice.utility_tariff_csv ?? "",
          demand_charge_strategy: demandChargeStrategy,
          ratchet_percentage: ratchetPercentage,
          lookback_months: lookbackMonths,
          historical_peak_kw: historicalPeakKw,
          frd_rate: frdRate,
          summer_trd_rate: summerTrdRate,
          winter_trd_rate: winterTrdRate,
        });
      } catch (err) {
        console.error("Error fetching device details for edit:", err);
        toast.error("Failed to load meter details for editing. Please try again.");
        closeModal();
      } finally {
        setIsEditingLoading(false);
      }
    },
    [storageAccountName, accessToken, closeModal],
  );

  const handleChange = useCallback((e) => {
    const { id, name, value } = e.target;
    const key = name || id;
    if (!key) return;
    setFormData((prev) => ({ ...prev, [key]: value }));
    setErrors((prev) => {
      const next = { ...prev };
      delete next[key];
      return next;
    });
  }, []);

  const handleFileChange = useCallback(
    async (e) => {
      const file = e.target.files[0];
      if (!file || !file.name.endsWith(".csv")) {
        toast.error("Please upload a valid .csv file");
        return;
      }

      setFormData((prev) => ({ ...prev, csv_file_name: file.name }));
      setUploading(true);

      try {
        const simName =
          step === 3 && editingDesign?.name ? editingDesign.name : microgridBase;

        const fileContent = await file.text();
        const result = await uploadTariffCsv({
          storageAccountName,
          simulationName: simName,
          fileName: file.name,
          accessToken,
          fileContent,
        });

        setUploadMessage(result);

        if (result.type === "success") {
          setErrors((prev) => {
            const next = { ...prev };
            delete next.csv_file_name;
            delete next.uploadMessage;
            return next;
          });
        }

        try {
          const dateData = await parseCsvMetadata({ accessToken, userId, file, fileType: "tariff" });
          if (dateData.start_date && dateData.end_date) {
            setFormData((prev) => ({
              ...prev,
              start_date: dateData.start_date,
              end_date: dateData.end_date,
            }));
          }
        } catch (parseErr) {
          console.error("Error parsing CSV for date range:", parseErr);
        }
      } catch (err) {
        console.error("Upload failed:", err);
        setUploadMessage({
          type: "error",
          text: "Network/server error occurred during upload.",
        });
      } finally {
        setUploading(false);
      }
    },
    [storageAccountName, accessToken, userId, step, editingDesign, microgridBase],
  );

  const handleCreateMicrogrid = useCallback(
    async (e) => {
      e.preventDefault();
      setSubmitError("");
      setErrors({});

      if (!microgridName.trim()) {
        toast.error("Enter a Microgrid Name", {
          style: { background: "#fff", color: "#4B5563" },
        });
        return;
      }

      const lowerCaseName = microgridName.toLocaleLowerCase().trim();
      const fullName = `${customerPrefix}-${lowerCaseName}`;
      const MAX_CONTAINER_LENGTH = 63;
      const maxUserPartLength = MAX_CONTAINER_LENGTH - (customerPrefix.length + 1);
      const azureContainerNameRegex = new RegExp(
        `^(?!-)(?!.*--)[a-z0-9-]{3,${maxUserPartLength}}(?<!-)$`,
      );

      if (!azureContainerNameRegex.test(lowerCaseName)) {
        setSubmitError(
          `Invalid Microgrid Name. Rules: 3-${maxUserPartLength} chars allowed for this customer, ` +
            "Must start and end with a letter/number, no spaces, no uppercase, no consecutive hyphens.",
        );
        return;
      }

      setIsSubmitting(true);
      try {
        const blobList = await fetchMicrogridList({
          storageAccountName,
          accessToken,
        });
        const exists = Object.keys(blobList).includes(fullName);
        if (exists) {
          toast.error("Microgrid name already exists. Please enter another microgrid name.");
        } else {
          sessionStorage.setItem("microgridbase", fullName);
          sessionStorage.setItem("microgridName", lowerCaseName);
          setMicrogridBase(fullName);
          setFormData({ ...INITIAL_FORM_DATA });
          setStep(2);
        }
      } catch (err) {
        console.error("Error checking microgrid name:", err);
        setSubmitError("Error checking microgrid name. Please try again.");
      } finally {
        setIsSubmitting(false);
      }
    },
    [microgridName, customerPrefix, storageAccountName, accessToken],
  );

  const handleSaveMeterDetails = useCallback(
    async (e) => {
      e.preventDefault();
      const validationErrors = validateMeterFields(
        formData,
        uploadMessage,
        existingPrimaryDemandCharge,
      );
      if (Object.keys(validationErrors).length > 0) {
        setErrors(validationErrors);
        return;
      }

      setIsSubmitting(true);
      setSubmitError("");

      const simName = step === 3 ? editingDesign?.name : microgridBase;
      const demandChargePayload = buildDemandChargeObject(formData, existingPrimaryDemandCharge);
      const demandChargesListPayload = buildDemandChargesList(formData, existingTouCharges);

      try {
        let payload;
        if (step === 2) {
          const meterPayload = {
            name: formData.meter_Name || "",
            make_and_model: formData.make_and_model || "",
            serial_number: formData.serial_number || "",
            utility_tariff_csv: formData.csv_file_name || "",
            building_load: { historical_load_csv: "" },
            evs: [],
            batteries: [],
            solar_arrays: [],
            fueled_generators: [],
            controllable_loads: [],
            uncontrollable_loads: [],
            ...(formData.start_date && { start_date: formData.start_date }),
            ...(formData.end_date && { end_date: formData.end_date }),
          };
          if (demandChargePayload) {
            meterPayload.demand_charge = demandChargePayload;
          }
          if (demandChargesListPayload) {
            meterPayload.demand_charges = demandChargesListPayload;
          }
          payload = { Microgrid: { meters: [meterPayload] } };
        } else if (step === 3) {
          const existingPayload = await fetchMicrogridSysconfig({
            storageAccountName,
            simulationName: editingDesign.name,
            accessToken,
          });
          const meters = existingPayload?.Microgrid?.meters;
          if (!Array.isArray(meters) || meters.length === 0) {
            toast.error("No meters in sysconfig. Cannot save.");
            return;
          }

          let meterToUpdate = meters[0];
          if (editingDesign.meterName) {
            meterToUpdate =
              meters.find((m) => (m.name || "").trim() === String(editingDesign.meterName).trim()) ||
              meters[0];
          }

          meterToUpdate.name = formData.meter_Name ?? meterToUpdate.name;
          meterToUpdate.make_and_model = formData.make_and_model ?? meterToUpdate.make_and_model;
          meterToUpdate.serial_number = formData.serial_number ?? meterToUpdate.serial_number;
          meterToUpdate.utility_tariff_csv = formData.csv_file_name ?? meterToUpdate.utility_tariff_csv;
          if (formData.start_date) meterToUpdate.start_date = formData.start_date;
          if (formData.end_date) meterToUpdate.end_date = formData.end_date;
          if (demandChargePayload) {
            meterToUpdate.demand_charge = demandChargePayload;
          } else {
            delete meterToUpdate.demand_charge;
          }
          if (demandChargesListPayload) {
            meterToUpdate.demand_charges = demandChargesListPayload;
          } else {
            delete meterToUpdate.demand_charges;
          }
          meterToUpdate.building_load = meterToUpdate.building_load || { historical_load_csv: "" };
          const consumptionLoad = (meterToUpdate.uncontrollable_loads || []).find(
            (c) => c.historical_load_csv,
          );
          if (consumptionLoad?.historical_load_csv) {
            meterToUpdate.building_load.historical_load_csv = consumptionLoad.historical_load_csv;
          }
          payload = existingPayload;
        }

        await saveMicrogridSysconfig({
          storageAccountName,
          simulationName: simName,
          accessToken,
          payload,
        });

        toast.success(
          `Meter details ${step === 2 ? "created" : "updated"} successfully.`,
          { style: { background: "#fff", color: "#4B5563" } },
        );
        closeModal();
        onSuccess?.();
      } catch (err) {
        console.error("Error saving meter details:", err);
        toast.error("Could not save configuration. Please try again later.");
      } finally {
        setIsSubmitting(false);
      }
    },
    [
      formData,
      uploadMessage,
      step,
      editingDesign,
      microgridBase,
      storageAccountName,
      accessToken,
      closeModal,
      onSuccess,
      existingPrimaryDemandCharge,
      existingTouCharges,
    ],
  );

  return {
    showModal,
    step,
    microgridName,
    setMicrogridName,
    formData,
    errors,
    submitError,
    isSubmitting,
    uploading,
    uploadMessage,
    isEditingLoading,
    existingPrimaryDemandCharge,
    openModal,
    closeModal,
    openEdit,
    handleChange,
    handleFileChange,
    handleCreateMicrogrid,
    handleSaveMeterDetails,
  };
}
